<template>
  <div
    v-if="parentType"
    :data-track-id="uuid"
    class="relative"
    :class="{
      'border-b border-b-border py-4 tablet:py-7 desktop:py-10': isDivided,
    }"
  >
    <UnpublishedWarning v-if="isTeaserNodeUnpublished" is-in-grid />
    <Component
      :is="teaserComponent"
      v-bind="teaserProps"
      :class="{
        'has-number': isNumbered,
      }"
    >
      <template v-if="options.teaserMedia === 'zurichCard'" #media>
        <ZurichCardImage
          :id="uuid"
          :no-animation="isEditing"
          class="block aspect-4/3"
        />
      </template>
    </Component>
  </div>
  <section v-else :id class="container">
    <UnpublishedWarning v-if="isTeaserNodeUnpublished" />
    <ZtLink
      v-if="teaserProps"
      :to="teaserProps.url"
      class="paragraph-teaser-full group-link group/teaser relative my-spacing-md block overflow-hidden"
    >
      <MediaImage
        v-if="teaserProps.media"
        v-blokkli-droppable:field_image
        v-bind="teaserProps.media"
        responsive-style="content_fullwidth"
        class="transition duration-300 ease-swing group-hover/teaser:scale-105"
      />
      <div
        class="paragraph-teaser-full-text absolute bottom-0 left-0 w-full p-8"
      >
        <h3 v-blokkli-editable:field_title class="title is-h2 !mb-0 text-white">
          {{ teaserProps.title }}
        </h3>
        <p
          v-if="teaserProps.text"
          v-blokkli-editable:field_text
          class="mt-3 max-w-prose text-prose text-balance text-white"
        >
          {{ teaserProps.text }}
        </p>
        <div
          v-blokkli-editable="{ name: 'field_link.title' }"
          class="link with-arrow mt-5 text-prose font-bold text-white group-hover/teaser:text-white"
        >
          {{ teaserProps.linkTitle || $texts('teaserMore') }}
        </div>
      </div>
    </ZtLink>
  </section>
</template>

<script lang="ts" setup>
import type { ParagraphTeaserFragment } from '#graphql-operations'
import type { TeaserProps } from '~/components/Teaser/types'
import {
  TeaserFull,
  TeaserHalf,
  TeaserThird,
  TeaserQuarter,
  TeaserFifth,
} from '#components'
import { buildTeaserProps } from './helper'

const props = defineProps<ParagraphTeaserFragment>()

const { $texts } = useEasyTexts()
const user = await useDrupalUser()

const isEditing = false

const { options, parentType, index, siblings } = defineBlokkli({
  bundle: 'teaser',
  globalOptions: ['bkHiddenGlobally', 'publicationChannels'],
  options: {
    teaserMedia: {
      type: 'radios',
      label: 'Teaser Media',
      default: 'media',
      group: 'Advanced',
      options: {
        media: 'Media',
        zurichCard: 'Zürich Card',
      },
    },
    showOpeningHours: {
      type: 'checkbox',
      label: 'Show opening hours',
      default: true,
    },
  },

  editor: {
    previewWidth: 800,
  },

  propsFieldMapping: {
    link: {
      type: 'editable',
      name: 'field_link.title',
    },
    uuid: null,
    title: {
      type: 'editable',
      name: 'field_title',
    },
    tagline: {
      type: 'editable',
      name: 'field_tagline',
    },
    text: {
      type: 'editable',
      name: 'field_text',
    },

    image: {
      type: 'droppable',
      name: 'field_image',
    },
    video: null,
    address: null,
  },
})
const id = useParagraphAnchorId(index, siblings)

const teaserColumns = useParagraphContext('teaserColumns', 'full')
const isDivided = useParagraphContext('teaserDivided', false)
const isNumbered = useParagraphContext('teaserNumbered', false)

const teaserComponent = computed(() => {
  switch (teaserColumns.value) {
    case 'full':
      return TeaserFull
    case 'quarter':
      return TeaserQuarter
    case 'fifth':
      return TeaserFifth
    case 'half':
      return TeaserHalf
  }

  return TeaserThird
})

const teaserProps = computed<TeaserProps | null>(() => {
  if (!props) {
    return null
  }
  const teaserProps = buildTeaserProps(props)
  if (!teaserProps) {
    return null
  }

  return {
    ...teaserProps,
    showOpeningHours: options.value.showOpeningHours,
    editableConfig: isEditing
      ? {
          title: 'field_title',
          image: 'field_image',
          tagline: 'field_tagline',
          text: 'field_text',
          linkTitle: 'field_link.title',
        }
      : undefined,
  }
})

const isTeaserNodeUnpublished = computed(() => {
  return (
    user.canAccessToolbar &&
    teaserProps.value?.entity &&
    !teaserProps.value?.isPublished
  )
})
</script>

<style>
.paragraph-teaser-full-text {
  background: linear-gradient(
    rgba(0, 0, 0, 0),
    rgba(0, 0, 0, 0.5) 40%,
    rgba(0, 0, 0, 0.8)
  );
}
</style>
